import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { californiaCities } from "./lib/californiaData";
import { californiaCounties } from "./lib/californiaCountyData";
import { competitorComparisons } from "./lib/competitorComparisonData";
import LeadTrackingProvider from "./components/LeadTrackingProvider";
import PageTransitionLoader from "./components/PageTransitionLoader";
import { resetScrollTracking } from "./lib/analytics";

// Critical path - load immediately for best LCP
import Index from "./pages/Index";
const GetQuote = lazy(() => import("./pages/GetQuote"));

// Lazy load non-critical routes for better initial bundle size
const Confirmation = lazy(() => import("./pages/Confirmation"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Terms = lazy(() => import("./pages/Terms"));
const SmsOptIn = lazy(() => import("./pages/SmsOptIn"));
const DirectoryToolkit = lazy(() => import("./pages/DirectoryToolkit"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ServiceAreas = lazy(() => import("./pages/ServiceAreas"));
const ServiceArea = lazy(() => import("./pages/ServiceArea"));
const CountyArea = lazy(() => import("./pages/CountyArea"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminAuth = lazy(() => import("./pages/AdminAuth"));
const AdsRescueKit = lazy(() => import("./pages/AdsRescueKit"));
const Press = lazy(() => import("./pages/Press"));
const CaseStudies = lazy(() => import("./pages/CaseStudies"));
const EVCharging = lazy(() => import("./pages/EVCharging"));
const Tesla = lazy(() => import("./pages/Tesla"));
const Commercial = lazy(() => import("./pages/Commercial"));
const LearningCenter = lazy(() => import("./pages/LearningCenter"));

// Equipment Pages
const EquipmentTeslaPowerwall = lazy(() => import("./pages/EquipmentTeslaPowerwall"));
const EquipmentEnphaseIQ = lazy(() => import("./pages/EquipmentEnphaseIQ"));
const EquipmentFranklinPower = lazy(() => import("./pages/EquipmentFranklinPower"));

// SEO Authority & Answer Target Pages
const CaliforniaSolarStatistics = lazy(() => import("./pages/CaliforniaSolarStatistics"));
const CompareTeslaPowerwallVsEnphase = lazy(() => import("./pages/CompareTeslaPowerwallVsEnphase"));
const CompareUtilityVsPPA = lazy(() => import("./pages/CompareUtilityVsPPA"));
const Reviews = lazy(() => import("./pages/Reviews"));
const CompetitorComparison = lazy(() => import("./pages/CompetitorComparison"));

// Link Building & Developer Pages
const PressKit = lazy(() => import("./pages/PressKit"));
const ApiDocs = lazy(() => import("./pages/ApiDocs"));
const Glossary = lazy(() => import("./pages/Glossary"));
const Infographics = lazy(() => import("./pages/Infographics"));
const EmbedCalculator = lazy(() => import("./pages/EmbedCalculator"));
const EmbedSavingsBadge = lazy(() => import("./pages/EmbedSavingsBadge"));
const EmbedRateWidget = lazy(() => import("./pages/EmbedRateWidget"));
const PowerPurchaseAgreement = lazy(() => import("./pages/PowerPurchaseAgreement"));
const ResourcesForPartners = lazy(() => import("./pages/ResourcesForPartners"));
const BacklinkGapAnalyzer = lazy(() => import("./pages/BacklinkGapAnalyzer"));
const SEOToolkit = lazy(() => import("./pages/SEOToolkit"));
const SEOToolkitPro = lazy(() => import("./pages/SEOToolkitPro"));
const FAQPage = lazy(() => import("./pages/FAQ"));
const ContentOptimizer = lazy(() => import("./pages/ContentOptimizer"));
const SEODashboard = lazy(() => import("./pages/SEODashboard"));
const KeywordGapAnalysis = lazy(() => import("./pages/KeywordGapAnalysis"));
const ContentScheduler = lazy(() => import("./pages/ContentScheduler"));
const EnergySavingsCalculatorPage = lazy(() => import("./pages/EnergySavingsCalculatorPage"));

// Loading fallback component for lazy routes
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

// Scroll to top on route change and initial load
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    // Reset scroll tracking on route change
    resetScrollTracking();
  }, [pathname]);

  return null;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <LeadTrackingProvider>
          <PageTransitionLoader />
          <ScrollToTop />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/savings-estimate" element={<Navigate to="/" replace />} />
              <Route path="/energy-savings-calculator" element={<EnergySavingsCalculatorPage />} />
              <Route path="/get-quote" element={<GetQuote />} />
              <Route path="/power-purchase-agreement" element={<PowerPurchaseAgreement />} />
              <Route path="/confirmation" element={<Confirmation />} />
              <Route path="/sms-opt-in" element={<SmsOptIn />} />
              <Route path="/booking-confirmation" element={<BookingConfirmation />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<Terms />} />
              
              {/* Service Areas */}
              <Route path="/service-areas" element={<ServiceAreas />} />
              {californiaCities.map(city => (
                <Route key={city.slug} path={`/${city.slug}`} element={<ServiceArea />} />
              ))}
              
              {/* County Pages - 58 California Counties */}
              {californiaCounties.map(county => (
                <Route key={county.slug} path={`/${county.slug}`} element={<CountyArea />} />
              ))}
              
              <Route path="/directory-toolkit" element={<DirectoryToolkit />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              
              {/* Link-Building Pages - SEO/Backlink focused (unlisted) */}
              <Route path="/press" element={<Press />} />
              <Route path="/case-studies" element={<CaseStudies />} />
              
              {/* Service Pages - SEO focused (unlisted) */}
              <Route path="/ev-charging" element={<EVCharging />} />
              <Route path="/tesla" element={<Tesla />} />
              <Route path="/Tesla" element={<Navigate to="/tesla" replace />} />
              <Route path="/commercial" element={<Commercial />} />
              <Route path="/Commercial" element={<Navigate to="/commercial" replace />} />
              <Route path="/learning-center" element={<LearningCenter />} />
              
              {/* Equipment Specification Pages */}
              <Route path="/equipment/tesla-powerwall-3" element={<EquipmentTeslaPowerwall />} />
              <Route path="/equipment/enphase-iq-battery" element={<EquipmentEnphaseIQ />} />
              <Route path="/equipment/franklin-home-power" element={<EquipmentFranklinPower />} />
              
              {/* SEO Authority & Research Pages - Backlink Targets */}
              <Route path="/california-solar-statistics-2026" element={<CaliforniaSolarStatistics />} />
              
              {/* Comparison Pages - Answer Target / Featured Snippet Focus */}
              <Route path="/compare/tesla-powerwall-vs-enphase-battery" element={<CompareTeslaPowerwallVsEnphase />} />
              <Route path="/compare/utility-vs-ppa-california" element={<CompareUtilityVsPPA />} />
              
              {/* Link Building & Developer Pages */}
              <Route path="/press-kit" element={<PressKit />} />
              <Route path="/api-docs" element={<ApiDocs />} />
              <Route path="/glossary" element={<Glossary />} />
              <Route path="/infographics" element={<Infographics />} />
              <Route path="/embed/calculator" element={<EmbedCalculator />} />
              <Route path="/embed/savings-badge" element={<EmbedSavingsBadge />} />
              <Route path="/embed/rate-widget" element={<EmbedRateWidget />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/resources-for-partners" element={<ResourcesForPartners />} />
              <Route path="/tools/backlink-gap-analyzer" element={<BacklinkGapAnalyzer />} />
              <Route path="/tools/seo-toolkit" element={<SEOToolkit />} />
              <Route path="/tools/seo-toolkit-pro" element={<SEOToolkitPro />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/tools/content-optimizer" element={<ContentOptimizer />} />
              <Route path="/tools/seo-dashboard" element={<SEODashboard />} />
              <Route path="/tools/keyword-gap" element={<KeywordGapAnalysis />} />
              <Route path="/tools/content-scheduler" element={<ContentScheduler />} />
              
              {/* Competitor Comparison Pages */}
              {competitorComparisons.map(comp => (
                <Route key={comp.slug} path={`/compare/${comp.slug}`} element={<CompetitorComparison />} />
              ))}
              
              {/* Admin Routes - Not linked publicly */}
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin-auth" element={<AdminAuth />} />
              <Route path="/admin/ads-rescue" element={<AdsRescueKit />} />
              
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </LeadTrackingProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;